import * as React from 'react';
import { forwardRef, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import MediaQuery, { useMediaQuery } from 'react-responsive';

import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { startOfDay } from 'date-fns';

import { PriceGraphProps, Prices, RouteType } from '@features/price-graph';
import { Datepicker, DatepickerProps } from '../../Datepicker/components/Datepicker';
import { Provider as PriceMatrixProvider } from '../../PriceMatrix/theme';
import { priceGraphTheme, priceMatrixTheme } from '../theme';
import { MoneyType, PriceMatrixProps, PriceRT } from '../../PriceMatrix/types';
import { DialogAdapter } from './DialogAdapter/DialogAdapter';
import {
	disableAutocompleteProps,
	format,
	mergeCSS,
	MOBILE_MIN_WIDTH,
	removeDotsFromDate,
	TABLET_MIN_WIDTH
} from '../../utils';
import { EngineTheme, ThemeProvider, useTheme } from '../../theme';
import DummyPriceGraph from '../../Results/components/Results/Calendar/DummyPriceGraph/DummyPriceGraph';

import { CompactMoney, Props as MoneyProps, formatCompactMoneyAmount, getCompactAmountPattern } from '../../Money';
import { CurrencySymbols } from '../../enums';
import LinearProgress from '@mui/material/LinearProgress';

import EmptyDatesOverlay from './EmptyDatesOverlay/EmptyDatesOverlay';
import FooterButtons, { ButtonRendererProps } from './FooterButtons/FooterButtons';
import { useRenderers } from '../../renderProps';
import {
	DatepickerTab,
	HoverDates
} from '../../SearchForm/components/SearchForm/Segments/Segment/Datepicker/Datepicker';
import { isMultiCity } from '../../SearchForm/store/segments/selectors';
import { useConfig } from '../../context';
import { useCurrency } from '../../hooks';
import { useCalendarNormalization } from '../hooks';
import { CdnIcon } from '@shared/ui';
import { TabTypes } from '../types';

const PriceGraph = React.lazy<React.ComponentType<PriceGraphProps>>(() =>
	import('@features/price-graph').then(m => ({ default: m.PriceGraph }))
);

const PriceMatrix = React.lazy<React.ComponentType<PriceMatrixProps>>(() =>
	import('../../PriceMatrix/components/PriceMatrix')
);

export type Props = DatepickerProps & {
	isDateToSelected?: boolean;
	isDateBackSelected?: boolean;
	isClearable?: boolean;
	isDoneable?: boolean;
	onClear?: () => void;
	onHover?: (date: Date) => void;
	hoverDates?: HoverDates;
	showProgress?: boolean;
	onPriceGraphPricesEnd?: (date: Date, direction: 'next' | 'prev') => void;
	onDone?: () => void;
	pricesTo?: Prices;
	pricesBack?: Prices;
	matrixPrices?: PriceRT[];
	showPriceGraph?: boolean;
	showPriceMatrix?: boolean;
	onClose?: () => void;
	valueRenderer?: (isOpen: boolean, open?: () => void, close?: () => void) => React.ReactNode;
	inputValue?: string;
	inputClassName?: string;
	inputFocusedClassName?: string;
	inputPlaceholder?: string;
	datepickerAlternativeHighlighting?: boolean;
	outsideClickIgnoreClass?: string;
	clearOpenDate?: () => void;
	onOpen?: () => void;
	useThemeWithPrices?: boolean;
	routeType?: 'outbound' | 'return';
	onRTSelect?: (dateTo: Date, dateBack: Date) => void;
	onSelect?: (date: Date, isBackDate?: boolean) => void;
	closeAfterSelectReturnDate?: ((date: Date) => boolean) | false;
	onClickOutboundDate?: React.MouseEventHandler;
	onClickReturnDate?: React.MouseEventHandler;
	moneyRoundType?: MoneyProps['roundType'];
};

const SearchFormDatepicker = React.memo<Props>(
	forwardRef<HTMLDivElement, Props>((props, ref) => {
		const DatepickerOverride = useRenderers().DatepickerInsideSearchForm ?? Datepicker;
		const { t } = useTranslation('SearchFormDatepicker');

		const [tab, setTab] = useState(TabTypes.PlainCalendar),
			[isOpen, toggleOpen] = useState(false),
			outDate = props.selectedDates && props.selectedDates.length ? new Date(props.selectedDates[0]) : null,
			returnDate =
				props.selectedDates && props.selectedDates.length > 1 ? new Date(props.selectedDates[1]) : null,
			isTabDisplay = props.showPriceMatrix || props.showPriceGraph,
			isMobile = useMediaQuery({ maxWidth: +MOBILE_MIN_WIDTH + 1 }),
			isTablet = useMediaQuery({ maxWidth: 1024 }),
			isNarrowDesktop = useMediaQuery({ maxWidth: 1160 });

		const isMultyCity = useSelector(isMultiCity);

		const {
			SearchFormDatepickerCompactMoney: SearchFormDatepickerCompactMoneyOverride = CompactMoney
		} = useRenderers();

		const {
			SearchForm: { separatedDatepicker }
		} = useConfig();

		const showButtons = !separatedDatepicker || isTablet || !!outDate;

		const refEl = useRef<HTMLInputElement>(null);
		const contentRef = React.createRef<HTMLDivElement>();

		const onClose = () => {
			toggleOpen(false);
			props.clearOpenDate && props.clearOpenDate();

			if (props.onClose) {
				props.onClose();
			}
		};

		const onDone = () => {
			toggleOpen(false);

			if (props.onDone) {
				props.onDone();
			}
		};

		const open = () => {
			toggleOpen(true);
			props.onOpen?.();
		};

		const onSelect = (date: Date) => {
			if (props.onSelect) {
				props.onSelect(date, props.routeType === 'return');
			}

			const shouldClose =
				separatedDatepicker || (!!props.closeAfterSelectReturnDate && props.closeAfterSelectReturnDate(date));

			if (shouldClose) {
				onDone();
			}
		};

		const { Datepicker: searchFormDatepickerTheme } = useTheme('SearchForm');
		const { SearchFormDatepickerStyles: datepickerTheme, ...defaultDatepickerTheme } = useTheme(
			'SearchFormDatepicker'
		);

		let showOverlay = false;

		if (props.routeType === DatepickerTab.Outbound && props.pricesTo === null) {
			showOverlay = true;
		} else if (props.routeType === DatepickerTab.Return && (props.pricesTo === null || props.pricesBack === null)) {
			showOverlay = true;
		}
		if (!isMobile) {
			showOverlay = false;
		}

		const pricesLoaded = (): boolean => {
			if (props.routeType === 'outbound' && Object.keys(props.pricesTo).length) {
				return true;
			} else if (
				props.routeType === 'return' &&
				Object.keys(props.pricesTo).length &&
				Object.keys(props.pricesBack).length
			) {
				return true;
			}

			return false;
		};

		const datepickerStyles = useTheme('Datepicker');
		const legendStyles = useTheme('Features').PriceGraph.LegendsStyles;

		const datePickerWithPricesTheme: EngineTheme = {
			Datepicker: {
				...defaultDatepickerTheme,
				WeeklyHeaderStyles: {
					...defaultDatepickerTheme.WeeklyHeaderStyles,
					dayOfWeekName: defaultDatepickerTheme.WeeklyHeaderStyles.dayOfWeekName_withPrices
				},
				MonthStyles: {
					...defaultDatepickerTheme.MonthStyles,
					week: defaultDatepickerTheme.MonthStyles.week_withPrice
				},
				DayStyles: {
					...defaultDatepickerTheme.DayStyles,
					day: defaultDatepickerTheme.DayStyles.day_withPrice,
					day_endHover: defaultDatepickerTheme.DayStyles.day_endHover_withPrice,
					day_startHover: defaultDatepickerTheme.DayStyles.day_startHover_withPrice
				}
			}
		};

		const customStyles: EngineTheme = {
			Datepicker: {
				...defaultDatepickerTheme,
				DayStyles: {
					...defaultDatepickerTheme.DayStyles,
					day: props.datepickerAlternativeHighlighting
						? datepickerTheme.day
						: defaultDatepickerTheme.DayStyles.day
				},
				MonthsWrapperStyles: {
					...defaultDatepickerTheme.MonthsWrapperStyles,
					wrapper: defaultDatepickerTheme.MonthsWrapperStyles.wrapper,
					nav_prev: defaultDatepickerTheme.MonthsWrapperStyles.nav_prev,
					nav_next: defaultDatepickerTheme.MonthsWrapperStyles.nav_next
				}
			}
		};

		const defaultStyles = {
			Datepicker: {
				...datepickerStyles
			}
		};

		// todo: react.memo does not make deep compare, if we want to memoise mergedTheme, we can pass JSON.stringify(defaultStyles), JSON.strignify(customStyles) do deps array
		const mergedTheme = React.useMemo(
			() => mergeCSS(defaultStyles, props.useThemeWithPrices ? datePickerWithPricesTheme : customStyles),
			[defaultStyles, customStyles]
		);

		const { currency } = useCurrency();

		const compactPattern = getCompactAmountPattern(currency);

		const currencyName = t(`currency_name_${currency}`, {
			defaultValue: CurrencySymbols[currency] || currency
		});

		const dayWithPrice = (date: Date) => {
			const compareString = date.toISOString();

			let price: MoneyType = null;
			const pricesMap = props.routeType === 'outbound' ? props.pricesTo : props.pricesBack;
			const hasFlight = pricesMap?.[compareString]?.hasFlight;

			if (pricesMap && pricesMap[compareString] && pricesMap[compareString]?.price?.amount) {
				price = pricesMap[compareString].price;
			}

			return (
				<div className={cn({ [defaultDatepickerTheme.DayStyles.day_hasPrice]: price || hasFlight })}>
					{date.getDate()}
					{price && (
						<SearchFormDatepickerCompactMoneyOverride
							moneyClassName={cn(
								defaultDatepickerTheme.DayStyles.day__price,
								searchFormDatepickerTheme.day__price
							)}
							money={price}
							showCurrency={false}
							compactAmountRenderer={(amount, _currency, limit) =>
								formatCompactMoneyAmount(amount, currency, limit, compactPattern)
							}
						/>
					)}
				</div>
			);
		};

		const handleOverlayButtonClick = React.useCallback(() => {
			setTab(TabTypes.PlainCalendar);
		}, []);

		const monthsHeader =
			!isMultyCity && !returnDate ? (
				<div
					className={cn(datepickerTheme.selectDateHint, {
						[datepickerTheme.selectDateHint_return]: !!outDate
					})}
				>
					{t(!!outDate ? 'Select the return flight date' : 'Select the depart flight date')}
				</div>
			) : null;

		const buttonRendererProps: ButtonRendererProps = {
			notActive: props.isClearable,
			onClear: props.onClear,
			onClose,
			isDoneable: props.isDoneable,
			onDone,
			tab,
			routeType: props.routeType,
			isDateBackSelected: props.isDateBackSelected,
			isDateToSelected: props.isDateToSelected,
			isMultyCity
		};

		const desktopMonthCount = isNarrowDesktop ? 2 : props.monthCount ?? 3;

		const { isOutDateInPast, normalizedSelectedDates, normalizedOpenDate } = useCalendarNormalization(
			outDate,
			props.selectedDates,
			props.openDate,
			isMobile,
			desktopMonthCount
		);

		React.useEffect(() => {
			if (isOpen && isOutDateInPast && props.onSelect) {
				const today = startOfDay(new Date());
				props.onSelect(today, false);
			}
		}, [isOpen, isOutDateInPast, props.onSelect]);

		return (
			<ThemeProvider value={mergedTheme}>
				<div ref={ref} className={datepickerTheme.inputWrapper}>
					<input
						placeholder={props.inputPlaceholder}
						className={classnames(props.inputClassName, { [props.inputFocusedClassName]: isOpen })}
						onClick={open}
						ref={refEl}
						value={props.inputValue}
						readOnly={true}
						{...disableAutocompleteProps}
					/>

					{props.valueRenderer && props.valueRenderer(isOpen, open, onClose)}
				</div>

				<DialogAdapter
					onClose={onClose}
					isOpen={isOpen}
					outsideClickIgnoreClass={props.outsideClickIgnoreClass}
					popupClassName={datepickerTheme.dialog}
				>
					<div
						className={classnames(datepickerTheme.popup, {
							[datepickerTheme.popup_withoutTabs]: !isTabDisplay
						})}
						data-search-form-datepicker={true}
						data-test-id="datepicker"
					>
						{props.showProgress && (
							<LinearProgress
								variant="indeterminate"
								classes={{
									root: datepickerTheme.progress__root,
									barColorPrimary: datepickerTheme.progress__bar__color,
									colorPrimary: datepickerTheme.progress__color
								}}
							/>
						)}
						<MediaQuery maxWidth={TABLET_MIN_WIDTH}>
							<div className={datepickerTheme.dialog__bar}>
								<div className={datepickerTheme.dialog__dates} onClick={onClose}>
									<span
										className={classnames({
											[datepickerTheme.dialog__activeDate]: props.routeType === 'outbound'
										})}
										onClick={e => {
											e.stopPropagation();
											props.onClickOutboundDate(e);
										}}
									>
										<span>
											{outDate
												? removeDotsFromDate(format(outDate, 'd MMM, EEEEEE'))
												: t('Outbound')}
										</span>
									</span>

									{outDate && (
										<span
											className={classnames(datepickerTheme.dialog__dateBack, {
												[datepickerTheme.dialog__activeDate]: props.routeType === 'return'
											})}
											onClick={e => {
												e.stopPropagation();
												props.onClickReturnDate(e);
											}}
										>
											<span>
												{returnDate
													? removeDotsFromDate(format(returnDate, 'd MMM, EEEEEE'))
													: t('Return')}
											</span>
										</span>
									)}
								</div>

								<div onClick={onClose} className={datepickerTheme.dialog__close} />
							</div>
						</MediaQuery>

						{props.useThemeWithPrices && (
							<MediaQuery maxWidth={TABLET_MIN_WIDTH}>
								<div className={datepickerTheme.datepicker__disclaimer}>
									<CdnIcon
										className={datepickerTheme.datepicker__disclaimerIcon}
										icon="Info/Details"
									/>
									<span className={datepickerTheme.datepicker__disclaimerText}>
										{t('Currency is specified in {{currency}}', {
											currency: currencyName
										})}
									</span>
								</div>
							</MediaQuery>
						)}

						<div className={classnames(datepickerTheme.content)} ref={contentRef}>
							{tab === TabTypes.PlainCalendar && (
								<>
									<MediaQuery minWidth={+TABLET_MIN_WIDTH + 1}>
										<DatepickerOverride
											onSelect={onSelect}
											onHover={props.onHover}
											hoverDates={props.hoverDates}
											openDate={normalizedOpenDate}
											highlightedDates={props.highlightedDates}
											selectedDates={normalizedSelectedDates}
											monthCount={desktopMonthCount}
											minDate={props.minDate}
											maxDate={props.maxDate}
											dayRenderer={props.useThemeWithPrices && dayWithPrice}
											monthsHeader={monthsHeader}
										/>
									</MediaQuery>

									<MediaQuery maxWidth={TABLET_MIN_WIDTH}>
										<DatepickerOverride
											getParentRef={() => contentRef}
											onSelect={onSelect}
											onHover={props.onHover}
											hoverDates={props.hoverDates}
											openDate={normalizedOpenDate}
											highlightedDates={props.highlightedDates}
											selectedDates={normalizedSelectedDates}
											monthCount={12}
											monthBaseDateVariant="min"
											minDate={props.minDate}
											maxDate={props.maxDate}
											dayRenderer={props.useThemeWithPrices && dayWithPrice}
										/>
									</MediaQuery>
								</>
							)}
							{tab === TabTypes.PriceGraph &&
								(showOverlay ? (
									<EmptyDatesOverlay onClick={handleOverlayButtonClick} />
								) : (
									<ThemeProvider
										value={{
											Features: {
												PriceGraph: {
													...priceGraphTheme,
													LegendsStyles: legendStyles
												}
											}
										}}
									>
										<React.Suspense fallback={<p>...</p>}>
											{props.showPriceGraph && pricesLoaded() ? (
												<PriceGraph
													outDate={outDate}
													backDate={returnDate}
													openDate={
														props.selectedDates && props.selectedDates.length
															? props.selectedDates[0]
															: props.openDate
													}
													onPricesEnd={props.onPriceGraphPricesEnd}
													onSelect={onSelect}
													prices={
														props.routeType === 'return' ? props.pricesBack : props.pricesTo
													}
													routeType={
														props.routeType === 'return'
															? RouteType.Return
															: RouteType.Outbound
													}
												/>
											) : (
												<DummyPriceGraph />
											)}
										</React.Suspense>
									</ThemeProvider>
								))}
							{tab === TabTypes.RoundtripMatrix && (
								<PriceMatrixProvider value={priceMatrixTheme}>
									<React.Suspense fallback={<p>...</p>}>
										<PriceMatrix
											prices={props.matrixPrices}
											size={7}
											onDateSelect={props.onRTSelect}
										/>
									</React.Suspense>
								</PriceMatrixProvider>
							)}
						</div>

						<MediaQuery minWidth={TABLET_MIN_WIDTH}>
							<div className={datepickerTheme.actionButtons}>
								{props.useThemeWithPrices && (
									<div className={datepickerTheme.datepicker__disclaimer}>
										<CdnIcon
											className={datepickerTheme.datepicker__disclaimerIcon}
											icon="Info/Details"
										/>
										<span className={datepickerTheme.datepicker__disclaimerText}>
											{t('Approximate prices for 1 person for 1 direction')}
										</span>
										|
										<CdnIcon
											className={datepickerTheme.datepicker__disclaimerIcon}
											icon="Info/Details"
										/>
										<span className={datepickerTheme.datepicker__disclaimerText}>
											{t('Currency is specified in {{currency}}', {
												currency: currencyName
											})}
										</span>
									</div>
								)}

								<FooterButtons
									showButtons={showButtons}
									outDate={outDate}
									returnDate={returnDate}
									buttonRendererProps={buttonRendererProps}
								/>
							</div>
						</MediaQuery>

						{isTabDisplay && (
							<div className={datepickerTheme.footer}>
								<Tabs
									value={tab}
									onChange={(ecent, newValue) => setTab(newValue)}
									classes={{
										root: datepickerTheme.tabs,
										indicator: datepickerTheme.tab__indicator
									}}
								>
									<Tab
										disableRipple={true}
										classes={{
											root: datepickerTheme.tab,
											wrapped: datepickerTheme.tab__wrapper,
											selected: datepickerTheme.tab_selected
										}}
										label={t('Calendar')}
										icon={
											<MediaQuery maxWidth={TABLET_MIN_WIDTH}>
												<CdnIcon icon="Action/Show Calendar" />
											</MediaQuery>
										}
									/>

									{props.showPriceGraph && (
										<Tab
											disableRipple={true}
											classes={{
												root: datepickerTheme.tab,
												wrapped: datepickerTheme.tab__wrapper,
												selected: datepickerTheme.tab_selected
											}}
											label={t('Price graph')}
											icon={
												<MediaQuery maxWidth={TABLET_MIN_WIDTH}>
													<CdnIcon icon="Action/Show Price Graph" />
												</MediaQuery>
											}
										/>
									)}

									{props.showPriceMatrix && (
										<Tab
											disableRipple={true}
											classes={{
												root: classnames(datepickerTheme.tab, datepickerTheme.tab_matrix),
												selected: datepickerTheme.tab_selected
											}}
											label={t('Price matrix')}
										/>
									)}
								</Tabs>
							</div>
						)}

						<MediaQuery maxWidth={TABLET_MIN_WIDTH - 1}>
							<FooterButtons
								showButtons={showButtons}
								outDate={outDate}
								returnDate={returnDate}
								buttonRendererProps={buttonRendererProps}
							/>
						</MediaQuery>
					</div>
				</DialogAdapter>
			</ThemeProvider>
		);
	})
);

export default SearchFormDatepicker;
